import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "The Experience", id: "experience" },
  { label: "What's Included", id: "whats-included" },
  { label: "About Kay", id: "about" },
  { label: "Contact", id: "contact" },
];

export default function Navbar({ onSchedule }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/80 backdrop-blur-2xl border-b border-border shadow-[0_10px_30px_-10px_rgba(43,48,51,0.08)]" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 text-left"
        >
          <span className="h-9 w-9 rounded-full bg-coral grid place-items-center text-cream font-poppins font-bold">
            K
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-poppins font-bold text-charcoal text-lg">
              Community Experiences<span className="text-coral"> by Kay</span>
            </span>
            <span data-testid="header-kwaasa-line" className="mt-1 text-[0.65rem] md:text-xs font-semibold uppercase tracking-[0.18em] text-charcoal/55">
              A Kwaasa Experience
            </span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-charcoal/70 hover:text-charcoal font-semibold transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-schedule-btn"
            onClick={onSchedule}
            className="rounded-full bg-coral text-cream font-semibold px-6 py-3 transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_30px_-12px_rgba(240,125,106,0.6)]"
          >
            Request a 15-Minute Conversation
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden h-11 w-11 grid place-items-center rounded-full bg-cream border border-border text-charcoal"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-cream border-b border-border"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left text-charcoal font-semibold text-lg"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-schedule-btn"
                onClick={() => { setOpen(false); onSchedule(); }}
                className="rounded-full bg-coral text-cream font-semibold px-6 py-3 mt-2"
              >
                Request a 15-Minute Conversation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
